import React, {
  useEffect,
  useState,
} from "react";

import {
  FaLayerGroup,
} from "react-icons/fa";

import { toast } from "react-toastify";

import EditorDrawerShell from "./EditorDrawerShell";

const INPUT =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-violet-400 focus:ring-4 focus:ring-violet-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:ring-violet-900/40";

const INITIAL_FORM = {
  slug: "",
  label: "",
  titre: "",
  description: "",
  roles_cibles: "",
  afficher_sommaire: true,
  ordre: 10,
  is_active: true,
};

function rolesToString(roles) {
  if (!roles) {
    return "";
  }

  if (Array.isArray(roles)) {
    return roles.join(", ");
  }

  if (typeof roles === "string") {
    try {
      const parsed = JSON.parse(roles);

      return Array.isArray(parsed)
        ? parsed.join(", ")
        : roles;
    } catch {
      return roles;
    }
  }

  return "";
}

export default function TutorialSectionDrawer({
  drawer,
  saving,
  onClose,
  onSave,
}) {
  const isOpen =
    drawer.open &&
    drawer.type === "section";

  const [formData, setFormData] =
    useState(INITIAL_FORM);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (
      drawer.mode === "edit" &&
      drawer.data
    ) {
      setFormData({
        slug: drawer.data.slug || "",
        label: drawer.data.label || "",
        titre: drawer.data.titre || "",
        description:
          drawer.data.description || "",

        roles_cibles: rolesToString(
          drawer.data.roles_cibles
        ),

        afficher_sommaire:
          drawer.data
            .afficher_sommaire !== false,

        ordre: drawer.data.ordre || 10,

        is_active:
          drawer.data.is_active !== false,
      });
    } else {
      setFormData(INITIAL_FORM);
    }
  }, [isOpen, drawer]);

  const handleChange = (event) => {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    setFormData((current) => ({
      ...current,
      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.slug.trim()) {
      toast.error(
        "Le slug de la section est obligatoire."
      );
      return;
    }

    if (!formData.label.trim()) {
      toast.error(
        "Le libellé du sommaire est obligatoire."
      );
      return;
    }

    if (!formData.titre.trim()) {
      toast.error(
        "Le titre de la section est obligatoire."
      );
      return;
    }

    const roles = formData.roles_cibles
      .split(",")
      .map((role) =>
        role.trim().toLowerCase()
      )
      .filter(Boolean);

    await onSave({
      slug: formData.slug
        .trim()
        .toLowerCase(),
      label: formData.label.trim(),
      titre: formData.titre.trim(),
      description:
        formData.description.trim() ||
        null,

      roles_cibles:
        roles.length > 0 ? roles : null,

      afficher_sommaire:
        formData.afficher_sommaire,

      ordre:
        Number(formData.ordre) || 0,

      is_active: formData.is_active,
    });
  };

  return (
    <EditorDrawerShell
      open={isOpen}
      eyebrow={
        drawer.mode === "edit"
          ? "Modification"
          : "Nouvelle section"
      }
      title={
        drawer.mode === "edit"
          ? "Modifier la section"
          : "Ajouter une section"
      }
      description="Une section regroupe plusieurs étapes et apparaît dans le sommaire du tutoriel."
      icon={<FaLayerGroup />}
      saving={saving}
      submitLabel={
        drawer.mode === "edit"
          ? "Enregistrer"
          : "Créer la section"
      }
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      <div className="space-y-6">
        <FormSection
          title="Identification"
          description="Définis l’ancre, le titre et le libellé de navigation."
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field>
              <Label required>
                Slug
              </Label>

              <input
                className={INPUT}
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                placeholder="utilisateurs"
              />
            </Field>

            <Field>
              <Label required>
                Libellé du sommaire
              </Label>

              <input
                className={INPUT}
                name="label"
                value={formData.label}
                onChange={handleChange}
                placeholder="Les Pages Utilisateurs"
              />
            </Field>

            <Field className="md:col-span-2">
              <Label required>
                Titre
              </Label>

              <input
                className={INPUT}
                name="titre"
                value={formData.titre}
                onChange={handleChange}
                placeholder="Les Pages Utilisateurs"
              />
            </Field>

            <Field className="md:col-span-2">
              <Label>Description</Label>

              <textarea
                className={`${INPUT} min-h-[130px] resize-y leading-6`}
                name="description"
                value={
                  formData.description
                }
                onChange={handleChange}
              />
            </Field>
          </div>
        </FormSection>

        <FormSection
          title="Affichage"
          description="Ordre, rôles concernés et visibilité."
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field>
              <Label>Ordre</Label>

              <input
                type="number"
                min="0"
                step="10"
                className={INPUT}
                name="ordre"
                value={formData.ordre}
                onChange={handleChange}
              />
            </Field>

            <Field>
              <Label>Rôles ciblés</Label>

              <input
                className={INPUT}
                name="roles_cibles"
                value={
                  formData.roles_cibles
                }
                onChange={handleChange}
                placeholder="utilisateur, enseignant"
              />
            </Field>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
            <ToggleCard
              name="afficher_sommaire"
              checked={
                formData.afficher_sommaire
              }
              onChange={handleChange}
              title="Afficher dans le sommaire"
              description="Ajoute un lien vers cette section."
            />

            <ToggleCard
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              title="Section active"
              description="Autorise son affichage sur les sites."
            />
          </div>
        </FormSection>
      </div>
    </EditorDrawerShell>
  );
}

function FormSection({
  title,
  description,
  children,
}) {
  return (
    <section className="rounded-[26px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6">
      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
        {title}
      </h3>

      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        {description}
      </p>

      <div className="mt-5">
        {children}
      </div>
    </section>
  );
}

function Field({
  children,
  className = "",
}) {
  return (
    <div className={className}>
      {children}
    </div>
  );
}

function Label({
  children,
  required = false,
}) {
  return (
    <label className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">
      {children}

      {required ? (
        <span className="ml-1 text-rose-500">
          *
        </span>
      ) : null}
    </label>
  );
}

function ToggleCard({
  name,
  checked,
  onChange,
  title,
  description,
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/50">
      <div>
        <div className="font-semibold text-slate-900 dark:text-slate-50">
          {title}
        </div>

        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {description}
        </div>
      </div>

      <input
        type="checkbox"
        name={name}
        checked={checked}
        onChange={onChange}
        className="h-5 w-5 rounded border-slate-300 text-violet-600"
      />
    </label>
  );
}